import { useCallback } from 'react';
import type { AgentModelSupportHelpers } from '@/hooks/config/useAgentModelSupport';
import type { AgentTypeRegistry } from '@/lib/agent-type-registry';
import type { AgentType } from '@/lib/types';

export interface UseLaunchAgentDefaultsOptions {
  modelSupport: AgentModelSupportHelpers;
  agentTypeRegistry: AgentTypeRegistry;
}

export interface UseLaunchAgentDefaultsResult {
  getDefaultProviderForAgentType: (type: AgentType) => string | null;
  getDefaultModelForProvider: (type: AgentType, providerId: string | null) => string;
  getDefaultModelForAgentType: (type: AgentType) => string;
  getDefaultEndpointForModel: (type: AgentType, modelId: string) => string | null;
  getDefaultEndpointForAgentType: (type: AgentType) => string | null;
}

/**
 * Provides default resolution functions for agent type, provider, model, and endpoint
 * selection in the Launch dialog.
 *
 * These functions encapsulate the cascading logic for determining sensible defaults
 * when switching agent types, providers, or models.
 */
export function useLaunchAgentDefaults({
  modelSupport,
  agentTypeRegistry,
}: UseLaunchAgentDefaultsOptions): UseLaunchAgentDefaultsResult {
  const getDefaultProviderForAgentType = useCallback(
    (type: AgentType): string | null => {
      if (!modelSupport.isMatrixDriven(type)) return null;
      const providers = modelSupport.getProvidersForAgent(type);
      if (providers.length <= 1) return providers[0]?.id ?? null;
      const curatedDefault = modelSupport.getDefaultProvider(type);
      if (curatedDefault && providers.some((p) => p.id === curatedDefault)) {
        return curatedDefault;
      }
      return providers[0]?.id ?? null;
    },
    [modelSupport],
  );

  const getDefaultModelForProvider = useCallback(
    (type: AgentType, providerId: string | null): string => {
      if (!modelSupport.isMatrixDriven(type)) {
        return agentTypeRegistry.getDefaultModel(type) ?? '';
      }
      const curatedDefaultModel = modelSupport.getDefaultModel(type);
      if (curatedDefaultModel) {
        const curatedModelProviderId = modelSupport.getProviderForModel(type, curatedDefaultModel);
        if (!providerId || curatedModelProviderId === providerId) {
          return curatedDefaultModel;
        }
      }
      if (providerId) {
        const modelsForProvider = modelSupport.getModelsForProvider(type, providerId);
        return modelsForProvider[0]?.id ?? '';
      }
      return modelSupport.getDiscoveredModels(type)[0]?.id ?? '';
    },
    [modelSupport, agentTypeRegistry],
  );

  const getDefaultModelForAgentType = useCallback(
    (type: AgentType): string => {
      const defaultProvider = getDefaultProviderForAgentType(type);
      return getDefaultModelForProvider(type, defaultProvider);
    },
    [getDefaultProviderForAgentType, getDefaultModelForProvider],
  );

  const getDefaultEndpointForModel = useCallback(
    (type: AgentType, modelId: string): string | null => {
      if (!modelSupport.isMatrixDriven(type) || !modelId) return null;
      const nextProviderId = modelSupport.getProviderForModel(type, modelId);
      const nextEndpoints = modelSupport.getEndpointsForProvider(type, nextProviderId);
      const availableEndpointsForModel = nextEndpoints.filter((e) =>
        modelSupport.isEndpointAvailable(type, e.id),
      );
      const defaultEndpoint = modelSupport.getDefaultEndpoint(type);
      if (defaultEndpoint && availableEndpointsForModel.some((e) => e.id === defaultEndpoint)) {
        return defaultEndpoint;
      }
      return availableEndpointsForModel[0]?.id ?? null;
    },
    [modelSupport],
  );

  const getDefaultEndpointForAgentType = useCallback(
    (type: AgentType): string | null => {
      return getDefaultEndpointForModel(type, getDefaultModelForAgentType(type));
    },
    [getDefaultEndpointForModel, getDefaultModelForAgentType],
  );

  return {
    getDefaultProviderForAgentType,
    getDefaultModelForProvider,
    getDefaultModelForAgentType,
    getDefaultEndpointForModel,
    getDefaultEndpointForAgentType,
  };
}
